import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import { Button } from '../components/ui/button';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { Panel } from '../components/ui/panel';
import { useCurrentUser, useLogin } from '../features/auth/session';
import { ApiError } from '../lib/api';

export function LoginPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: user, isPending: sessionPending } = useCurrentUser();
  const login = useLogin();

  const usernameId = useId();
  const passwordId = useId();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  if (!sessionPending && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  const errorMessage =
    login.error instanceof ApiError
      ? login.error.code === 'network_error'
        ? t('login.networkError')
        : login.error.message
      : login.error
        ? t('login.failed')
        : null;

  return (
    <main className="grid min-h-dvh place-items-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 rounded-panel border halftone px-5 py-6 text-center">
          <h1 className="text-4xl leading-none text-foreground">{t('app.name')}</h1>
          <p className="mt-2 text-sm text-muted">{t('app.tagline')}</p>
        </div>

        <Panel className="p-6">
          <h2 className="text-2xl text-foreground">{t('login.title')}</h2>
          <p className="mt-1 mb-6 text-sm text-muted">{t('login.subtitle')}</p>

          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              login.mutate({ username, password });
            }}
          >
            <Field label={t('login.username')} htmlFor={usernameId}>
              <Input
                id={usernameId}
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </Field>

            <Field label={t('login.password')} htmlFor={passwordId}>
              <Input
                id={passwordId}
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            {errorMessage ? (
              <p role="alert" className="text-sm text-danger">
                {errorMessage}
              </p>
            ) : null}

            <Button type="submit" size="lg" disabled={login.isPending} className="mt-2">
              {login.isPending ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>
        </Panel>
      </div>
    </main>
  );
}
